import { useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { ArrowLeft, ArrowRight, Check, FileText, X } from "lucide-react";
import { useLabEvent, useLabsExperiment } from "./labExperiment";

const steps = [
  {
    title: "Collect at home.",
    label: "At your place",
    image: "/images/tasso/collect.webp",
    alt: "At-home collection device on an upper arm",
    copy: "Your kit includes an at-home blood collection device. Follow the preparation and collection instructions included with your kit.",
  },
  {
    title: "Return your sample.",
    label: "On its way",
    image: "/images/tasso/return.webp",
    alt: "Person holding a small System return pouch",
    copy: "Package your sample as directed and send it to the laboratory. Follow available delivery updates in your System portal.",
  },
  {
    title: "See your results.",
    label: "In your portal",
    copy: "Open Labs to review your measurements and download your laboratory report as a PDF when they are available. Your care team can help you understand your report and next steps.",
  },
];

/** An illustrative portal UI, never a patient result or clinical interpretation. */
export function LabResultsIllustration() {
  return (
    <div className="bg-sunken p-20 sm:p-40 h-full flex items-center justify-center">
      <div className="bg-page border border-line-subtle w-full max-w-[38rem] shadow-sm">
        <div className="flex items-center justify-between p-15 border-b border-line-subtle">
          <span className="font-display text-24">system</span>
          <span className="type-label-xs text-ink-muted">Example results</span>
        </div>
        <div className="p-20">
          <p className="type-label-xs text-ink-muted">Your Labs</p>
          <h3 className="font-display text-24 mt-8">A clearer picture.</h3>
          <p className="type-body-sm text-ink-secondary mt-8">
            Your laboratory report, in one place.
          </p>
          <div className="flex gap-10 items-center bg-raised border border-line-subtle p-12 mt-20">
            <Check size={16} aria-hidden />
            <span className="type-body-sm">Results available</span>
          </div>
          {["Heart health", "Blood sugar", "Inflammation", "Hormones"].map(
            (name) => (
              <div
                key={name}
                className="border-b border-line-subtle py-15 flex items-center justify-between gap-12"
              >
                <span className="type-body-sm">{name}</span>
                <div aria-hidden className="flex gap-3">
                  <span className="w-25 h-4 bg-accent" />
                  <span className="w-15 h-4 bg-line-subtle" />
                </div>
              </div>
            ),
          )}
          <div className="mt-20 flex items-center justify-between type-body-sm">
            <span className="flex items-center gap-8">
              <FileText size={15} />
              Download PDF
            </span>
            <ArrowRight size={15} />
          </div>
        </div>
      </div>
    </div>
  );
}

export function LabHowItWorks({
  source,
  className = "",
  cardAction = false,
}: {
  source: string;
  className?: string;
  cardAction?: boolean;
}) {
  const { enabled } = useLabsExperiment();
  const emit = useLabEvent();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  if (!enabled) return null;
  const active = steps[step];
  function changeStep(next: number) {
    setStep(next);
    emit("blood_test_guide_step_viewed", { source, step: next + 1 });
  }
  return (
    <Dialog.Root
      open={open}
      onOpenChange={(value) => {
        setOpen(value);
        if (value) {
          setStep(0);
          emit("blood_test_guide_opened", { source });
        }
      }}
    >
      <Dialog.Trigger
        className={`min-h-44 inline-flex items-center justify-between gap-10 text-14 ${cardAction ? "flex-1 px-15 py-15 bg-ink text-white border-r border-white/25" : "underline"} ${className}`}
      >
        How it works <ArrowRight size={14} aria-hidden />
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-dialog bg-black/20 backdrop-blur-[10px]" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-dialog w-[min(92rem,calc(100vw-20px))] max-h-[92dvh] -translate-x-1/2 -translate-y-1/2 flex flex-col overflow-hidden border border-line-subtle bg-page">
          <header className="flex justify-between items-center border-b border-line-subtle bg-sunken">
            <Dialog.Title className="type-label-md px-20 py-15">
              Blood testing · How it works
            </Dialog.Title>
            <Dialog.Close
              aria-label="Close blood testing guide"
              className="h-50 w-50 flex shrink-0 items-center justify-center border-l border-line-subtle"
            >
              <X size={18} />
            </Dialog.Close>
          </header>
          <Dialog.Description className="sr-only">
            From collecting your sample at home to viewing your results in
            System.
          </Dialog.Description>
          <div className="overflow-y-auto min-h-0">
            <div className="grid md:grid-cols-2">
              <div
                className={`${active.image ? "aspect-[5/4]" : ""} md:aspect-[4/5] overflow-hidden bg-sunken`}
              >
                {active.image ? (
                  <img
                    src={active.image}
                    alt={active.alt}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <LabResultsIllustration />
                )}
              </div>
              <div className="p-20 sm:p-30 flex flex-col justify-between gap-25">
                <div>
                  <p className="type-label-xs text-ink-muted">
                    0{step + 1} / {active.label}
                  </p>
                  <h2 className="font-display text-28 md:text-36 mt-12 leading-110">
                    {active.title}
                  </h2>
                  <p className="type-body-md text-ink-secondary leading-150 mt-15">
                    {active.copy}
                  </p>
                </div>
                <div
                  className="grid grid-cols-3 gap-8"
                  aria-label="Blood test guide steps"
                >
                  {steps.map((item, i) => (
                    <button
                      key={item.title}
                      onClick={() => changeStep(i)}
                      aria-current={i === step ? "step" : undefined}
                      className="min-h-50 text-left border-t-2 border-line-subtle aria-[current=step]:border-ink pt-10 type-label-xs text-ink-muted aria-[current=step]:text-ink"
                    >
                      0{i + 1}
                      <span className="block mt-6">
                        {["Collect", "Return", "Results"][i]}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <footer className="flex justify-between border-t border-line-subtle p-12">
            <button
              disabled={step === 0}
              onClick={() => changeStep(step - 1)}
              className="min-h-44 px-12 flex items-center gap-8 type-body-sm disabled:opacity-30"
            >
              <ArrowLeft size={15} />
              Previous
            </button>
            {step < 2 ? (
              <button
                onClick={() => changeStep(step + 1)}
                className="min-h-44 px-20 bg-ink text-page flex items-center gap-10 type-body-sm"
              >
                Next
                <ArrowRight size={15} />
              </button>
            ) : (
              <Dialog.Close className="min-h-44 px-20 bg-ink text-page type-body-sm">
                Done
              </Dialog.Close>
            )}
          </footer>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
